import "promise-polyfill/src/polyfill";
import "unfetch/polyfill";
import "abortcontroller-polyfill";
import "./styles/index.scss";
import * as React from "react";
import { ReactElement } from "react";
import { CssBaseline } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import monitor from "@/utils/monitoring";
import { theme } from "@/styles/stylesheet";
import { AppRoutes } from "./AppRoutes";
import { BugFallbackPage } from "./pages/Error/BugFallbackPage";
import CookieNotice from "./components/organisms/cookie";
import onLoadSetup from "./init";
import { BrowserRouter } from "react-router-dom";
import { env } from "./env";
import { BrandNameProvider } from "./contexts/BrandingContext";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { BackendStatusProvider, useBackendContext } from "./store/BackendStatusProvider";

export const App = (): ReactElement => {
  React.useMemo(
    () =>
      onLoadSetup({
        SETTLD_ENV: env.SETTLD_ENV,
        GA_ID: env.GA_ID,
        GTM_ID: env.GTM_ID,
      }),
    []
  );
  return (
    <BrandNameProvider>
      <BrowserRouter>
        <BackendStatusProvider>
          <BackendActive>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <monitor.ErrorBoundary fallback={() => <BugFallbackPage />}>
                  <AppRoutes />
                  <CookieNotice />
                  <ToastContainer newestOnTop={false} rtl={false} pauseOnFocusLoss pauseOnHover />
                </monitor.ErrorBoundary>
              </LocalizationProvider>
            </ThemeProvider>
          </BackendActive>
        </BackendStatusProvider>
      </BrowserRouter>
    </BrandNameProvider>
  );
};

interface BackendActiveProps {
  children: React.ReactNode;
}

export const BackendActive = ({ children }: BackendActiveProps): JSX.Element | null => {
  const {
    state: { online },
  } = useBackendContext();
  // Show a loading indicator while checking the backend status.
  if (online === null) return <>Checking backend status...</>;
  // Do not render children if the backend is offline.
  if (!online) return <BugFallbackPage />;
  return <>{children}</>;
};
